import type { WorkoutCompletion } from "@/types";

// ─── Types ────────────────────────────────────────────────────────────────────

export type PrDetectionResult = {
  /** Durations where this ride beats the existing record. */
  newPRs: Record<string, { old: number; new: number }>;
  /** Durations within nearPct of the existing record but not beaten. */
  nearPRs: Record<string, { current: number; existing: number; pctAway: number }>;
};

// ─── Duration label display ───────────────────────────────────────────────────

const DURATION_LABEL: Record<string, string> = {
  "5s":   "5-sec",
  "1min": "1-min",
  "5min": "5-min",
  "20min": "20-min",
  "60min": "60-min",
};

export function label(key: string): string {
  return DURATION_LABEL[key] ?? key;
}

// ─── Core detection ───────────────────────────────────────────────────────────

/**
 * Pure function — no side effects.
 * Compares a completion's power data against existing power records.
 * Returns which durations set a new PR and which are within nearPct of one.
 *
 * IMPORTANT: Call this BEFORE updatePowerRecords() — that function synchronously
 * mutates the Zustand profile state, so reading profile.powerRecords afterwards
 * would compare new watts against the already-updated values.
 */
export function detectPrResults(
  completion: Pick<WorkoutCompletion, "bestEfforts" | "powerNpWatts">,
  existingRecords: Record<string, number>,
  nearPct = 0.05,
): PrDetectionResult {
  // Build candidate watts per duration key
  const candidates: Record<string, number> = {};

  if (completion.powerNpWatts && completion.powerNpWatts > 0) {
    candidates["20min"] = completion.powerNpWatts;
  }

  const b = completion.bestEfforts;
  if (b) {
    if (b.sprint5s && b.sprint5s > 0) candidates["5s"] = b.sprint5s;
    if (b.min1 && b.min1 > 0) candidates["1min"] = b.min1;
    if (b.vo2_5min && b.vo2_5min > 0) candidates["5min"] = b.vo2_5min;
    if (b.threshold20min && b.threshold20min > 0) {
      // Take max of NP proxy and exact 20-min best effort
      candidates["20min"] = Math.max(candidates["20min"] ?? 0, b.threshold20min);
    }
    if (b.endurance60min && b.endurance60min > 0) candidates["60min"] = b.endurance60min;
  }

  const newPRs: PrDetectionResult["newPRs"] = {};
  const nearPRs: PrDetectionResult["nearPRs"] = {};

  for (const [key, watts] of Object.entries(candidates)) {
    const existing = existingRecords[key] ?? 0;
    if (watts > existing) {
      newPRs[key] = { old: existing, new: watts };
    } else if (existing > 0 && watts < existing) {
      // Only show near-PR when strictly below the record; watts === existing
      // is a tie — silent, not worth a "so close!" message.
      const pctAway = (existing - watts) / existing;
      if (pctAway <= nearPct) {
        nearPRs[key] = { current: watts, existing, pctAway };
      }
    }
  }

  return { newPRs, nearPRs };
}

// ─── Toast message builders ───────────────────────────────────────────────────

/**
 * Builds the toast payload for new PR(s).
 * Returns null when result.newPRs is empty.
 * Multiple PRs → one combined toast ("3 New Personal Records!").
 */
export function buildPrToast(
  result: PrDetectionResult,
): { title: string; message: string } | null {
  const keys = Object.keys(result.newPRs);
  if (keys.length === 0) return null;

  if (keys.length === 1) {
    const key = keys[0];
    const { old, new: newWatts } = result.newPRs[key];
    const diff = newWatts - old;
    const message =
      old > 0
        ? `${newWatts}W · Previous best ${old}W (+${diff}W)`
        : `${newWatts}W · First ever!`;
    return { title: `New PR — ${label(key)} power`, message };
  }

  const summary = keys.map((k) => `${label(k)}: ${result.newPRs[k].new}W`).join(" · ");
  return { title: `${keys.length} New Personal Records!`, message: summary };
}

/**
 * Builds the toast payload for the closest near-PR (within 5%).
 * Returns null when result.nearPRs is empty.
 * Only the single closest duration is shown (avoids toast overload).
 */
export function buildNearPrToast(
  result: PrDetectionResult,
): { title: string; message: string } | null {
  const keys = Object.keys(result.nearPRs);
  if (keys.length === 0) return null;

  const closest = keys.reduce((a, b) =>
    result.nearPRs[a].pctAway < result.nearPRs[b].pctAway ? a : b,
  );
  const { current, existing, pctAway } = result.nearPRs[closest];
  const pctStr = `${Math.max(1, Math.round(pctAway * 100))}%`;

  return {
    title: `So close! ${label(closest)} power: ${current}W`,
    message: `Just ${pctStr} off your best (${existing}W) — keep pushing!`,
  };
}
